/**
 * 取込パーサー（genome-core）から IndexedDB へ流し込むシンク。
 * チャンクは数個ずつまとめて 1 トランザクションで書き、メモリに全長を残さない（§0.1）。
 */

import {
  ChunkAccumulator,
  DEFAULT_CHUNK_SIZE,
  type GenomicFeatureV1,
  type ImportSink,
  type SequenceFooter,
  type SequenceHeader,
} from '@kensuke/genome-core';
import { putChunks, putFeatures, putSequenceMeta, type StoredSequenceMeta } from './geneAnalysisChunkStore.service';

/** 1 トランザクションで書くチャンク数（256 kbp × 16 ≒ 4 MB） */
const FLUSH_EVERY_CHUNKS = 16;

export type ImportedSequenceMeta = Omit<StoredSequenceMeta, 'key' | 'updatedAt'>;

export class IndexedDbImportSink implements ImportSink {
  readonly sequences: ImportedSequenceMeta[] = [];

  private current: {
    sequenceId: string;
    header: SequenceHeader;
    features: GenomicFeatureV1[];
    accumulator: ChunkAccumulator;
    buffered: Array<{ index: number; codes: Uint8Array }>;
  } | null = null;

  constructor(
    private readonly projectId: string,
    private readonly chunkSize: number = DEFAULT_CHUNK_SIZE
  ) {}

  beginSequence(header: SequenceHeader): void {
    const sequenceId = `seq-${this.sequences.length + 1}`;
    const buffered: Array<{ index: number; codes: Uint8Array }> = [];
    this.current = {
      sequenceId,
      header,
      features: [],
      buffered,
      accumulator: new ChunkAccumulator(async (index, codes) => {
        buffered.push({ index, codes: codes.slice() });
        if (buffered.length >= FLUSH_EVERY_CHUNKS) await this.flushBuffered();
      }, this.chunkSize),
    };
  }

  async appendCodes(codes: Uint8Array): Promise<void> {
    if (!this.current) throw new Error('beginSequence が呼ばれていません');
    await this.current.accumulator.push(codes);
  }

  addFeature(feature: GenomicFeatureV1): void {
    if (!this.current) throw new Error('beginSequence が呼ばれていません');
    this.current.features.push(feature);
  }

  async endSequence(footer: SequenceFooter): Promise<void> {
    if (!this.current) throw new Error('beginSequence が呼ばれていません');
    await this.current.accumulator.flush();
    await this.flushBuffered();

    const meta: ImportedSequenceMeta = {
      projectId: this.projectId,
      sequenceId: this.current.sequenceId,
      name: this.current.header.name,
      description: this.current.header.description,
      length: footer.length,
      topology: footer.topology,
      chunkSize: this.chunkSize,
      chunkCount: this.current.accumulator.chunkCount,
      featureCount: this.current.features.length,
    };

    await putFeatures(this.projectId, this.current.sequenceId, this.current.features);
    await putSequenceMeta(meta);
    this.sequences.push(meta);
    this.current = null;
  }

  private async flushBuffered(): Promise<void> {
    if (!this.current || this.current.buffered.length === 0) return;
    const batch = this.current.buffered.splice(0, this.current.buffered.length);
    await putChunks(this.projectId, this.current.sequenceId, batch);
  }
}
